import {
  extractRequestScopePath,
  scopeFlagsFromMode,
  scopeModeFromFlags,
  shouldAutoMarkThirdParty,
  type DomainScopeFlags,
  type DomainScopeFlagsInput,
  type DomainScopeMode,
} from '@terra/shared-asset-processing';
import {
  deriveDomainScopeFromAllowlistRules,
  hasAllowlistPathRules,
  isValidScopePath,
  normalizeScopePathInput,
  resolveDomainFunctionalityScope,
  resolveDomainTestingScope,
  splitPrimaryHostPathRules,
} from '@terra/shared-contracts-applications';
import { DomainSourceSchema } from '@terra/shared-schemas-orchestrator';
import type {
  DomainConfigWithRelations,
  DomainPathRule,
} from '@terra/shared-schemas-orchestrator';

export const PATH_SCOPE_KINDS = ['allowlist', 'blocklist'] as const;

export type PathScopeKind = (typeof PATH_SCOPE_KINDS)[number];

export type PathScopeEntry = {
  path: string;
  kind: PathScopeKind;
  isInFunctionalityScope: boolean;
  isInTestingScope: boolean;
  isThirdParty: boolean;
};

export type PathScopeDisplayState = 'all' | 'partial';

export type DomainConfig = Omit<DomainConfigWithRelations, 'pathRules'> & {
  pathRules?: DomainPathRule[];
};

export type PathScopeDraft = {
  path: string;
  kind: PathScopeKind;
  isInFunctionalityScope?: boolean;
  isInTestingScope?: boolean;
  isThirdParty?: boolean;
};

export type DomainScopeToggleField = 'functionality' | 'testing' | 'thirdParty';

const nextDomainScopeMode = ({
  field,
  enabled,
  current,
}: {
  field: DomainScopeToggleField;
  enabled: boolean;
  current: DomainScopeFlagsInput;
}): DomainScopeMode => {
  if (field === 'testing') {
    return enabled
      ? 'attack'
      : scopeModeFromFlags({
          isInFunctionalityScope: current.isInFunctionalityScope,
          isInTestingScope: false,
          isThirdParty: false,
        });
  }

  if (field === 'functionality') {
    return enabled ? 'interaction' : 'none';
  }

  return enabled
    ? 'third_party'
    : scopeModeFromFlags({
        isInFunctionalityScope: current.isInFunctionalityScope,
        isInTestingScope: current.isInTestingScope,
        isThirdParty: false,
      });
};

const exclusiveFlagsFromToggle = ({
  field,
  enabled,
  current,
}: {
  field: DomainScopeToggleField;
  enabled: boolean;
  current: DomainScopeFlagsInput;
}): DomainScopeFlags =>
  scopeFlagsFromMode(nextDomainScopeMode({ field, enabled, current }));

const getDomainPathRules = (domain: DomainConfig): DomainPathRule[] =>
  domain.pathRules ?? [];

const isAllowlistRule = (rule: DomainPathRule): boolean =>
  rule.scope !== 'BLOCKLIST';

const updateAllowlistRuleAtPath = ({
  domain,
  path,
  updateRule,
}: {
  domain: DomainConfig;
  path: string;
  updateRule: (rule: DomainPathRule) => DomainPathRule;
}): DomainConfig => ({
  ...domain,
  pathRules: getDomainPathRules(domain).map((rule) => {
    if (rule.path !== path || !isAllowlistRule(rule)) {
      return rule;
    }

    return updateRule(rule);
  }),
});

export const getScopeFlag = ({
  domain,
  field,
}: {
  domain: DomainConfig;
  field: DomainScopeToggleField;
}): boolean => {
  if (field === 'functionality') {
    return resolveDomainFunctionalityScope(domain);
  }

  if (field === 'testing') {
    return resolveDomainTestingScope(domain);
  }

  return domain.isThirdParty ?? false;
};

export const getPathScopeFlag = ({
  rule,
  field,
}: {
  rule: PathScopeEntry;
  field: DomainScopeToggleField;
}): boolean => {
  if (field === 'functionality') {
    return rule.isInFunctionalityScope;
  }

  if (field === 'testing') {
    return rule.isInTestingScope;
  }

  return rule.isThirdParty;
};

export const isFunctionalityToggleDisabled = ({
  domain,
  rule,
}: {
  domain?: DomainConfig;
  rule?: PathScopeEntry;
}): boolean => {
  if (rule) {
    return rule.isInTestingScope;
  }

  return domain ? getScopeFlag({ domain, field: 'testing' }) : false;
};

export const applyFunctionalityToggle = ({
  domain,
  enabled,
}: {
  domain: DomainConfig;
  enabled: boolean;
}): DomainConfig => {
  if (isFunctionalityToggleDisabled({ domain })) {
    return domain;
  }

  const pathRules = getDomainPathRules(domain);

  if (hasAllowlistPathRules(pathRules)) {
    return {
      ...domain,
      isInFunctionalityScope: enabled,
      isInTestingScope: null,
      isThirdParty: false,
    };
  }

  const resolved = exclusiveFlagsFromToggle({
    field: 'functionality',
    enabled,
    current: {
      isInFunctionalityScope: enabled,
      isInTestingScope: false,
      isThirdParty: false,
    },
  });

  return {
    ...domain,
    ...resolved,
  };
};

export const applyTestingToggle = ({
  domain,
  enabled,
}: {
  domain: DomainConfig;
  enabled: boolean;
}): DomainConfig => {
  if (hasAllowlistPathRules(getDomainPathRules(domain))) {
    return domain;
  }

  const resolved = exclusiveFlagsFromToggle({
    field: 'testing',
    enabled,
    current: {
      isInFunctionalityScope: resolveDomainFunctionalityScope(domain),
      isInTestingScope: resolveDomainTestingScope(domain),
      isThirdParty: false,
    },
  });

  return {
    ...domain,
    ...resolved,
  };
};

export const applyThirdPartyToggle = ({
  domain,
  enabled,
}: {
  domain: DomainConfig;
  enabled: boolean;
}): DomainConfig => {
  if (hasAllowlistPathRules(getDomainPathRules(domain))) {
    return domain;
  }

  const resolved = exclusiveFlagsFromToggle({
    field: 'thirdParty',
    enabled,
    current: {
      isInFunctionalityScope: resolveDomainFunctionalityScope(domain),
      isInTestingScope: resolveDomainTestingScope(domain),
      isThirdParty: domain.isThirdParty,
    },
  });

  return {
    ...domain,
    ...resolved,
  };
};

export const applyPathFunctionalityToggle = ({
  domain,
  path,
  enabled,
}: {
  domain: DomainConfig;
  path: string;
  enabled: boolean;
}): DomainConfig =>
  updateAllowlistRuleAtPath({
    domain,
    path,
    updateRule: (rule) => {
      if (rule.isInTestingScope) {
        return rule;
      }

      const resolved = exclusiveFlagsFromToggle({
        field: 'functionality',
        enabled,
        current: {
          isInFunctionalityScope: enabled,
          isInTestingScope: false,
          isThirdParty: false,
        },
      });

      return { ...rule, ...resolved };
    },
  });

export const applyPathTestingToggle = ({
  domain,
  path,
  enabled,
}: {
  domain: DomainConfig;
  path: string;
  enabled: boolean;
}): DomainConfig =>
  updateAllowlistRuleAtPath({
    domain,
    path,
    updateRule: (rule) => {
      const resolved = exclusiveFlagsFromToggle({
        field: 'testing',
        enabled,
        current: {
          isInFunctionalityScope: rule.isInFunctionalityScope,
          isInTestingScope: rule.isInTestingScope,
          isThirdParty: false,
        },
      });

      return { ...rule, ...resolved };
    },
  });

export const applyPathThirdPartyToggle = ({
  domain,
  path,
  enabled,
}: {
  domain: DomainConfig;
  path: string;
  enabled: boolean;
}): DomainConfig =>
  updateAllowlistRuleAtPath({
    domain,
    path,
    updateRule: (rule) => {
      const resolved = exclusiveFlagsFromToggle({
        field: 'thirdParty',
        enabled,
        current: {
          isInFunctionalityScope: rule.isInFunctionalityScope,
          isInTestingScope: rule.isInTestingScope,
          isThirdParty: rule.isThirdParty,
        },
      });

      return { ...rule, ...resolved };
    },
  });

export const applyPathDraftScopeToggle = <TDraft extends DomainScopeFlags>({
  draft,
  field,
  enabled,
}: {
  draft: TDraft;
  field: DomainScopeToggleField;
  enabled: boolean;
}): TDraft =>
  field === 'functionality' && draft.isInTestingScope
    ? draft
    : {
        ...draft,
        ...exclusiveFlagsFromToggle({ field, enabled, current: draft }),
      };

const normalizeApplicationUrl = (applicationUrl: string): string =>
  applicationUrl.startsWith('http')
    ? applicationUrl
    : `https://${applicationUrl}`;

export const getDomainConfigHostname = (domainValue: string): string | null => {
  const trimmed = domainValue.trim();

  if (!trimmed) {
    return null;
  }

  if (/^https?:\/\//i.test(trimmed)) {
    try {
      return new URL(trimmed).hostname || null;
    } catch {
      return null;
    }
  }

  return trimmed.split('/')[0] || null;
};

export const getApplicationUrlHostname = (
  applicationUrl: string,
): string | null => {
  const trimmed = applicationUrl.trim();

  if (!trimmed) {
    return null;
  }

  try {
    return new URL(normalizeApplicationUrl(trimmed)).hostname || null;
  } catch {
    return null;
  }
};

export const getApplicationUrlEntryPath = (
  applicationUrl: string,
): string | null => {
  const trimmed = applicationUrl.trim();

  if (!trimmed) {
    return null;
  }

  try {
    const scopePath = extractRequestScopePath(normalizeApplicationUrl(trimmed));

    return scopePath && scopePath !== '/' ? scopePath : null;
  } catch {
    return null;
  }
};

export const stripApplicationUrlToOrigin = (applicationUrl: string): string => {
  const trimmed = applicationUrl.trim();

  if (!trimmed) {
    return trimmed;
  }

  try {
    const url = new URL(normalizeApplicationUrl(trimmed));

    return `${url.protocol}//${url.host}/`;
  } catch {
    return trimmed;
  }
};

export const getPrimaryHostDomainEntry = ({
  domains,
  hostname,
}: {
  domains: DomainConfig[];
  hostname: string;
}): DomainConfig | undefined =>
  domains.find((entry) => {
    const entryHostname = getDomainConfigHostname(entry.domain);

    return entryHostname?.toLowerCase() === hostname.toLowerCase();
  });

export const excludesPrimaryHostDomains = ({
  domains,
  hostname,
}: {
  domains: DomainConfig[];
  hostname: string | null;
}): DomainConfig[] => {
  if (!hostname) {
    return domains;
  }

  return domains.filter((entry) => {
    const entryHostname = getDomainConfigHostname(entry.domain);

    return entryHostname?.toLowerCase() !== hostname.toLowerCase();
  });
};

export const getPrimaryHostRedirectTargetDomain = ({
  domains,
  hostname,
  redirectToUrl,
}: {
  domains: DomainConfig[];
  hostname: string | null;
  redirectToUrl?: string | null;
}): DomainConfig | undefined => {
  const redirectTargetHostname = redirectToUrl
    ? getApplicationUrlHostname(redirectToUrl)
    : null;

  if (!hostname || !redirectTargetHostname) {
    return undefined;
  }

  return domains.find((entry) => {
    const entryHostname = getDomainConfigHostname(entry.domain);

    return (
      !!entryHostname &&
      entryHostname.toLowerCase() !== hostname.toLowerCase() &&
      entryHostname.toLowerCase() === redirectTargetHostname.toLowerCase()
    );
  });
};

export const isDomainLinkedToPrimaryHost = ({
  domain,
  primaryHostname,
  redirectToUrl,
}: {
  domain: string;
  primaryHostname: string | null;
  redirectToUrl?: string | null;
}): boolean => {
  const domainHostname = getDomainConfigHostname(domain)?.toLowerCase();
  const redirectTargetHostname = redirectToUrl
    ? getApplicationUrlHostname(redirectToUrl)?.toLowerCase()
    : null;

  return Boolean(
    domainHostname &&
      (domainHostname === primaryHostname?.toLowerCase() ||
        domainHostname === redirectTargetHostname),
  );
};

export const getSecondaryDomainsForPrimaryHost = ({
  domains,
  hostname,
  redirectToUrl,
}: {
  domains: DomainConfig[];
  hostname: string | null;
  redirectToUrl?: string | null;
}): DomainConfig[] => {
  const redirectTarget = getPrimaryHostRedirectTargetDomain({
    domains,
    hostname,
    redirectToUrl,
  });

  return excludesPrimaryHostDomains({ domains, hostname }).filter(
    (entry) => entry !== redirectTarget,
  );
};

const isAllowlistPathRule = (rule: DomainPathRule): boolean =>
  rule.scope !== 'BLOCKLIST';

const toStoredPathRule = (
  rule: Pick<DomainPathRule, 'path' | 'scope'> &
    Partial<
      Pick<
        DomainPathRule,
        'isInFunctionalityScope' | 'isInTestingScope' | 'isThirdParty'
      >
    >,
): DomainPathRule => ({
  path: rule.path,
  scope: rule.scope,
  isInFunctionalityScope: rule.isInFunctionalityScope ?? false,
  isInTestingScope: rule.isInTestingScope ?? false,
  isThirdParty: rule.isThirdParty ?? false,
});

const buildDefaultPrimaryHostDomainEntry = (
  hostname: string,
): DomainConfig => ({
  domain: hostname,
  source: 'USER_PROVIDED',
  ...scopeFlagsFromMode('interaction'),
  pathRules: [],
  hasWaf: null,
  createdAt: new Date(),
  enabledAt: null,
});

export const resolvePrimaryHostAttackEnabled = ({
  applicationUrl,
  domains,
}: {
  applicationUrl: string;
  domains: DomainConfig[];
}): boolean => {
  const hostname = getApplicationUrlHostname(applicationUrl);

  if (!hostname) {
    return true;
  }

  const primaryHostEntry = getPrimaryHostDomainEntry({ domains, hostname });

  if (!primaryHostEntry) {
    return true;
  }

  const entryPathRule = splitPrimaryHostPathRules({
    applicationUrl,
    pathRules: primaryHostEntry.pathRules,
  }).entryPathRule;

  if (entryPathRule) {
    return entryPathRule.isInTestingScope;
  }

  return resolveDomainTestingScope(primaryHostEntry);
};

const syncPrimaryRedirectTargetAttackScope = ({
  domains,
  hostname,
  redirectToUrl,
  enabled,
}: {
  domains: DomainConfig[];
  hostname: string;
  redirectToUrl?: string | null;
  enabled: boolean;
}): DomainConfig[] => {
  const redirectTargetHostname = redirectToUrl
    ? getApplicationUrlHostname(redirectToUrl)
    : null;

  if (!redirectTargetHostname) {
    return domains;
  }

  return domains.map((entry) => {
    const entryHostname = getDomainConfigHostname(entry.domain);

    if (
      !entryHostname ||
      entryHostname.toLowerCase() === hostname.toLowerCase() ||
      entryHostname.toLowerCase() !== redirectTargetHostname.toLowerCase()
    ) {
      return entry;
    }

    return applyTestingToggle({ domain: entry, enabled });
  });
};

export const applyPrimaryHostAttackToggle = ({
  applicationUrl,
  domains,
  hostname,
  redirectToUrl,
  enabled,
}: {
  applicationUrl: string;
  domains: DomainConfig[];
  hostname: string;
  redirectToUrl?: string | null;
  enabled: boolean;
}): DomainConfig[] => {
  const existingEntry = getPrimaryHostDomainEntry({ domains, hostname });
  const withoutPrimaryHost = excludesPrimaryHostDomains({ domains, hostname });
  const pathRules = existingEntry?.pathRules ?? [];
  const { entryPathRule, extraPathRules } = splitPrimaryHostPathRules({
    applicationUrl,
    pathRules,
  });
  const usesAllowlistPartialScope = hasAllowlistPathRules(pathRules);
  const withRedirectTargetAttackScope = (
    updatedDomains: DomainConfig[],
  ): DomainConfig[] =>
    syncPrimaryRedirectTargetAttackScope({
      domains: updatedDomains,
      hostname,
      redirectToUrl,
      enabled,
    });

  if (enabled) {
    if (!existingEntry) {
      return domains;
    }

    if (!usesAllowlistPartialScope) {
      return withRedirectTargetAttackScope([
        ...withoutPrimaryHost,
        {
          ...existingEntry,
          domain: hostname,
          pathRules,
          ...scopeFlagsFromMode('attack'),
          enabledAt: existingEntry.enabledAt ?? new Date(),
        },
      ]);
    }

    const updatedPathRules = [
      ...(entryPathRule
        ? [
            toStoredPathRule({
              ...entryPathRule,
              ...scopeFlagsFromMode('attack'),
            }),
          ]
        : []),
      ...extraPathRules.map(toStoredPathRule),
    ];

    return withRedirectTargetAttackScope([
      ...withoutPrimaryHost,
      {
        ...existingEntry,
        domain: hostname,
        pathRules: updatedPathRules,
        isInFunctionalityScope: existingEntry.isInFunctionalityScope ?? null,
        isInTestingScope: null,
        isThirdParty: false,
      },
    ]);
  }

  if (!usesAllowlistPartialScope) {
    return withRedirectTargetAttackScope([
      ...withoutPrimaryHost,
      {
        ...(existingEntry ?? buildDefaultPrimaryHostDomainEntry(hostname)),
        domain: hostname,
        pathRules,
        ...scopeFlagsFromMode('interaction'),
      },
    ]);
  }

  const updatedPathRules = pathRules.map((rule) => {
    if (
      entryPathRule &&
      rule.path === entryPathRule.path &&
      isAllowlistPathRule(rule)
    ) {
      return {
        ...rule,
        ...scopeFlagsFromMode('interaction'),
      };
    }

    return rule;
  });

  return withRedirectTargetAttackScope([
    ...withoutPrimaryHost,
    {
      ...(existingEntry ?? buildDefaultPrimaryHostDomainEntry(hostname)),
      domain: hostname,
      pathRules: updatedPathRules,
      isInFunctionalityScope: existingEntry?.isInFunctionalityScope ?? null,
      isInTestingScope: null,
      isThirdParty: false,
    },
  ]);
};

export const updatePrimaryHostDomainEntry = ({
  domains,
  hostname,
  updater,
}: {
  domains: DomainConfig[];
  hostname: string;
  updater: (entry: DomainConfig) => DomainConfig;
}): DomainConfig[] => {
  const withoutPrimaryHost = excludesPrimaryHostDomains({ domains, hostname });
  const existingEntry =
    getPrimaryHostDomainEntry({ domains, hostname }) ??
    buildDefaultPrimaryHostDomainEntry(hostname);

  return [
    ...withoutPrimaryHost,
    updater({ ...existingEntry, domain: hostname }),
  ];
};

export const buildAddedDomainEntry = ({
  domain,
  primaryHost,
}: {
  domain: string;
  primaryHost?: string | null;
}): DomainConfig => {
  const isCatalogThirdParty = shouldAutoMarkThirdParty({
    host: domain,
    primaryHost,
  });
  const flags = scopeFlagsFromMode(
    isCatalogThirdParty ? 'third_party' : 'attack',
  );

  return {
    domain,
    source: DomainSourceSchema.enum.USER_PROVIDED,
    ...flags,
    pathRules: [],
    hasWaf: null,
    createdAt: new Date(),
    enabledAt: flags.isInTestingScope ? new Date() : null,
  };
};

const toPathScopeKind = (scope: DomainPathRule['scope']): PathScopeKind =>
  scope === 'BLOCKLIST' ? 'blocklist' : 'allowlist';

const toPathScope = (kind: PathScopeKind): DomainPathRule['scope'] =>
  kind === 'blocklist' ? 'BLOCKLIST' : 'ALLOWLIST';

const toPathScopeEntry = (rule: DomainPathRule): PathScopeEntry => ({
  path: rule.path,
  kind: toPathScopeKind(rule.scope),
  isInFunctionalityScope: rule.isInFunctionalityScope,
  isInTestingScope: rule.isInTestingScope,
  isThirdParty: rule.isThirdParty,
});

const withResolvedDomainScope = ({
  domain,
  pathRules,
}: {
  domain: DomainConfig;
  pathRules: DomainPathRule[];
}): DomainConfig => {
  const isInTestingScope = resolveDomainTestingScope(domain);
  const isInFunctionalityScope = resolveDomainFunctionalityScope(domain);

  return {
    ...domain,
    pathRules,
    isInFunctionalityScope,
    isInTestingScope,
  };
};

export const getPathScopeDisplayState = (
  domain: DomainConfig,
): PathScopeDisplayState =>
  getDomainPathRules(domain).length === 0 ? 'all' : 'partial';

export const getPathEntries = (domain: DomainConfig): PathScopeEntry[] =>
  getDomainPathRules(domain).map(toPathScopeEntry);

export const addPathToDomain = ({
  domain,
  path,
  kind,
  isInFunctionalityScope,
  isInTestingScope,
  isThirdParty,
}: {
  domain: DomainConfig;
  path: string;
  kind: PathScopeKind;
  isInFunctionalityScope?: boolean;
  isInTestingScope?: boolean;
  isThirdParty?: boolean;
}): DomainConfig => {
  const normalizedPath = normalizeScopePathInput(path);
  const scope = toPathScope(kind);
  const filteredRules = getDomainPathRules(domain).filter(
    (rule) => rule.path !== normalizedPath,
  );

  if (kind === 'blocklist') {
    return {
      ...domain,
      pathRules: [
        ...filteredRules,
        {
          path: normalizedPath,
          scope,
          ...scopeFlagsFromMode('none'),
        },
      ],
    };
  }

  const flags = scopeFlagsFromMode(
    isThirdParty
      ? 'third_party'
      : scopeModeFromFlags({
          isInTestingScope:
            isInTestingScope ?? getScopeFlag({ domain, field: 'testing' }),
          isInFunctionalityScope:
            isInFunctionalityScope ??
            getScopeFlag({ domain, field: 'functionality' }),
        }),
  );
  const pathRules = [
    ...filteredRules,
    {
      path: normalizedPath,
      scope,
      ...flags,
    },
  ];
  const hasRestrictingAllowlist = hasAllowlistPathRules(pathRules);

  return {
    ...domain,
    isInFunctionalityScope: hasRestrictingAllowlist
      ? domain.isInFunctionalityScope
      : resolveDomainFunctionalityScope(domain),
    isInTestingScope: hasRestrictingAllowlist ? null : domain.isInTestingScope,
    pathRules,
  };
};

export const addPathsToDomain = ({
  domain,
  paths,
}: {
  domain: DomainConfig;
  paths: PathScopeDraft[];
}): DomainConfig =>
  paths.reduce(
    (currentDomain, pathEntry) =>
      addPathToDomain({ domain: currentDomain, ...pathEntry }),
    domain,
  );

export const removePathFromDomain = ({
  domain,
  path,
}: {
  domain: DomainConfig;
  path: string;
}): DomainConfig => {
  const currentRules = getDomainPathRules(domain);
  const pathRules = currentRules.filter((rule) => rule.path !== path);

  if (pathRules.length === 0) {
    if (hasAllowlistPathRules(currentRules)) {
      const derived = deriveDomainScopeFromAllowlistRules(currentRules);

      return {
        ...domain,
        pathRules,
        isInFunctionalityScope:
          domain.isInFunctionalityScope === true
            ? true
            : derived.isInFunctionalityScope,
        isInTestingScope: derived.isInTestingScope,
      };
    }

    return withResolvedDomainScope({ domain, pathRules });
  }

  if (hasAllowlistPathRules(pathRules)) {
    return {
      ...domain,
      pathRules,
      isInFunctionalityScope: domain.isInFunctionalityScope,
      isInTestingScope: null,
    };
  }

  return withResolvedDomainScope({ domain, pathRules });
};

export const updatePathInDomain = ({
  domain,
  previousPath,
  nextPath,
  kind,
  isInFunctionalityScope,
  isInTestingScope,
  isThirdParty,
}: {
  domain: DomainConfig;
  previousPath: string;
  nextPath: string;
  kind: PathScopeKind;
  isInFunctionalityScope?: boolean;
  isInTestingScope?: boolean;
  isThirdParty?: boolean;
}): DomainConfig => {
  const normalizedNextPath = normalizeScopePathInput(nextPath);
  const currentRules = getDomainPathRules(domain);
  const existingRule = currentRules.find((rule) => rule.path === previousPath);

  if (!existingRule) {
    return domain;
  }

  const withoutPrevious = removePathFromDomain({ domain, path: previousPath });

  return addPathToDomain({
    domain: withoutPrevious,
    path: normalizedNextPath,
    kind,
    isInFunctionalityScope:
      isInFunctionalityScope ?? existingRule.isInFunctionalityScope,
    isInTestingScope: isInTestingScope ?? existingRule.isInTestingScope,
    isThirdParty: isThirdParty ?? existingRule.isThirdParty,
  });
};

export { isValidScopePath };
